import React, { useRef } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { RecentLocation } from "./searchStorage";

interface LocationSearchCardProps {
  type: "from" | "to";
  value: string;
  onChange: (code: string, name: string) => void;
  recentLocations: RecentLocation[];
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  placeholder: string;
  searchInput: string;
  setSearchInput: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
    code?: string;
    name?: string;
    isDisabled?: boolean;
  }>;
  isLoading: boolean;
  label: string;
  onSearch: (inputValue: string) => Promise<void> | void;
}

const LocationSearchCard: React.FC<LocationSearchCardProps> = ({
  type,
  value,
  onChange,
  recentLocations,
  isOpen,
  onClose,
  searchInput,
  setSearchInput,
  options,
  isLoading,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Close on outside click (only while open)
  useClickOutside(cardRef, onClose, isOpen);

  const handleSelectLocation = (code: string, name: string) => {
    onChange(code, name);
    setSearchInput("");
  };

  if (!isOpen) return null;

  const showRecentLocations =
    searchInput.trim().length === 0 && recentLocations.length > 0;
  const showSearchResults = options.length > 0;

  return (
    <div
      ref={cardRef}
      className={`absolute top-full mt-2.5 bg-white border border-gray-100/80 rounded-2xl shadow-2xl z-50 max-h-[380px] w-[320px] max-w-[calc(100vw-32px)] overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-150 ${
        type === "to" ? "-right-3.5 sm:-right-4" : "-left-3.5 sm:-left-4"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header with Close Button */}
      <div className="bg-[#F3F4F6] px-3.5 py-2.5 border-b border-gray-200/60 flex items-center justify-between shrink-0">
        <p className="text-xs font-bold text-[#374151] whitespace-nowrap">
          {type === "from"
            ? "Select Departure Airport"
            : "Select Arrival Airport"}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-black transition-colors p-0.5 rounded-lg"
          aria-label="Close"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content Section - Recent Locations or Search Results */}
      <div className="overflow-y-auto flex-1 p-2">
        {/* Loading Spinner indicator */}
        {isLoading && (
          <div className="flex justify-center items-center py-4">
            <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Recent Locations Section - Show when input is empty */}
        {showRecentLocations && !isLoading && (
          <div>
            <div className="px-3 py-1.5 mb-1">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                {type === "from" ? "Recent Airports" : "Recent Destinations"}
              </p>
            </div>
            <div className="space-y-0.5">
              {recentLocations.map((location) => {
                const isSelected = value === location.code;
                return (
                  <button
                    key={location.code}
                    type="button"
                    onClick={() =>
                      handleSelectLocation(location.code, location.name)
                    }
                    className={`w-full px-3 py-2 text-left transition-all border-b border-gray-100/60 last:border-b-0 ${
                      isSelected
                        ? "bg-[#FFFBEB] rounded-xl font-medium"
                        : "hover:bg-gray-50 rounded-xl"
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900 leading-snug">
                        {location.code}
                      </span>
                      <span className="text-xs text-gray-500 font-normal mt-0.5 truncate">
                        {location.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Results - Show options */}
        {showSearchResults && (!showRecentLocations || searchInput.trim().length > 0) && (
          <div className="space-y-0.5">
            {options
              .filter((option) => !option.isDisabled)
              .map((option) => {
                const code = option.value;
                const name =
                  option.name ||
                  (option.label.includes(" - ")
                    ? option.label.split(" - ")[1]
                    : option.label);
                const isSelected = value === code;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelectLocation(code, name)}
                    className={`w-full px-3 py-2 text-left transition-all border-b border-gray-100/60 last:border-b-0 ${
                      isSelected
                        ? "bg-[#FFFBEB] rounded-xl font-medium"
                        : "hover:bg-gray-50 rounded-xl"
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900 leading-snug">
                        {code}
                      </span>
                      <span className="text-xs text-gray-500 font-normal mt-0.5 truncate">
                        {name}
                      </span>
                    </div>
                  </button>
                );
              })}
          </div>
        )}

        {/* No Results Message */}
        {searchInput.trim().length > 0 &&
          !isLoading &&
          options.length === 0 && (
            <div className="text-center py-6 text-gray-500 text-xs font-medium">
              No airports found
            </div>
          )}
      </div>
    </div>
  );
};

export default LocationSearchCard;
